// Three-step bank-holiday import wizard.
//
// Mirrors the legacy modal in `manage_holidays.php`. Fetches the public
// gov.uk bank-holiday feed client-side, asks the admin to pick which
// holidays + which employees to apply, and POSTs the bulk-import payload
// to the new server endpoint.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CalendarCheck2, Flag, Info, RefreshCw, X } from 'lucide-react';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { Checkbox } from '../../../../components/form/Checkbox';
import { Select } from '../../../../components/form/Select';
import { listStaff } from '../../../../api/users';
import { listDrivers } from '../../../../api/drivers';
import { queryKeys } from '../../../../api/queryKeys';
import { useBulkBankHolidays } from '../hooks/useHolidays';
import type { HolidayEntityType, HolidayPeriod } from '../../../../api/holidays';
import styles from './BankHolidayWizard.module.css';

interface BankHoliday {
  date: string;
  title: string;
}

interface GovUkBankHolidaysResponse {
  'england-and-wales': {
    events: Array<{ title: string; date: string }>;
  };
}

const BANK_HOLIDAYS_URL = 'https://www.gov.uk/bank-holidays.json';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function dayName(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return DAY_NAMES[d.getDay()] ?? '';
}

export interface BankHolidayWizardProps {
  open: boolean;
  onClose: () => void;
}

export function BankHolidayWizard({ open, onClose }: BankHolidayWizardProps): JSX.Element {
  const year = new Date().getFullYear();

  const holidaysQuery = useQuery<BankHoliday[]>({
    queryKey: ['bank-holidays', 'gov-uk', year],
    queryFn: async () => {
      const res = await fetch(BANK_HOLIDAYS_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as GovUkBankHolidaysResponse;
      const events = json['england-and-wales']?.events ?? [];
      return events
        .filter((e) => e.date.startsWith(String(year)))
        .map((e) => ({ date: e.date, title: e.title }));
    },
    enabled: open,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedHolidays, setSelectedHolidays] = useState<Set<string>>(new Set());
  const [entityType, setEntityType] = useState<HolidayEntityType | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());
  const [selectAllEntities, setSelectAllEntities] = useState(false);
  const [period, setPeriod] = useState<HolidayPeriod>('FULL');

  const staffQuery = useQuery({
    queryKey: queryKeys.users.list({ scope: 'wizard', status: 'active' }),
    queryFn: () => listStaff({ page: 1, limit: 200, status: 'active' }),
    enabled: open && entityType === 'staff',
    staleTime: 60_000,
  });
  const driversQuery = useQuery({
    queryKey: queryKeys.driversAdmin.list({ scope: 'wizard', status: 'active' }),
    queryFn: () => listDrivers({ page: 1, limit: 200, status: 'active' }),
    enabled: open && entityType === 'driver',
    staleTime: 60_000,
  });

  // Initialise: every fetched holiday selected by default (mirrors legacy).
  useEffect(() => {
    if (holidaysQuery.data) {
      setSelectedHolidays(new Set(holidaysQuery.data.map((h) => h.date)));
    }
  }, [holidaysQuery.data]);

  // Reset state on open/close.
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedHolidays(new Set());
      setEntityType(null);
      setSelectedEntities(new Set());
      setSelectAllEntities(false);
    }
  }, [open]);

  const importMut = useBulkBankHolidays();

  const peopleList = useMemo(() => {
    if (entityType === 'staff') {
      return (staffQuery.data?.staff ?? []).map((s) => ({
        uuid: s.uuid,
        name: s.name,
        email: s.email,
      }));
    }
    if (entityType === 'driver') {
      return (driversQuery.data?.drivers ?? []).map((d) => ({
        uuid: d.uuid,
        name: d.name,
        email: d.email,
      }));
    }
    return [];
  }, [entityType, staffQuery.data, driversQuery.data]);

  function toggleHoliday(date: string) {
    setSelectedHolidays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }

  function selectAllHolidays() {
    if (!holidaysQuery.data) return;
    setSelectedHolidays(new Set(holidaysQuery.data.map((h) => h.date)));
  }
  function deselectAllHolidays() {
    setSelectedHolidays(new Set());
  }

  function toggleEntity(uuid: string) {
    setSelectedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      // Keep "select all" checkbox in sync.
      if (peopleList.length > 0 && next.size === peopleList.length) {
        setSelectAllEntities(true);
      } else {
        setSelectAllEntities(false);
      }
      return next;
    });
  }

  function handleSelectAll(checked: boolean) {
    setSelectAllEntities(checked);
    if (checked) {
      setSelectedEntities(new Set(peopleList.map((p) => p.uuid)));
    } else {
      setSelectedEntities(new Set());
    }
  }

  function handleNext() {
    if (step === 1) {
      if (selectedHolidays.size === 0) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      if (entityType === null || selectedEntities.size === 0) return;
      setStep(3);
      return;
    }
  }

  function handleBack() {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  }

  function handleSubmit() {
    if (entityType === null) return;
    const datesToSubmit =
      holidaysQuery.data?.filter((h) => selectedHolidays.has(h.date)) ?? [];
    const isAll =
      peopleList.length > 0 && selectedEntities.size === peopleList.length;
    importMut.mutate(
      {
        entity_type: entityType,
        entity_uuids: isAll ? 'all' : Array.from(selectedEntities),
        dates: datesToSubmit,
        period,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  }

  const selectedHolidayList = (holidaysQuery.data ?? []).filter((h) =>
    selectedHolidays.has(h.date),
  );
  const selectedEntityList = peopleList.filter((p) => selectedEntities.has(p.uuid));

  const canNextStep1 = selectedHolidays.size > 0;
  const canNextStep2 = entityType !== null && selectedEntities.size > 0;

  const footer = (
    <div className={styles.footer}>
      {step > 1 ? (
        <Button variant="secondary" icon={ArrowLeft} onClick={handleBack}>
          Back
        </Button>
      ) : (
        <span />
      )}
      <div className={styles.footerRight}>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {step < 3 ? (
          <Button
            variant="primary"
            icon={ArrowRight}
            onClick={handleNext}
            disabled={(step === 1 && !canNextStep1) || (step === 2 && !canNextStep2)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="primary"
            icon={CalendarCheck2}
            onClick={handleSubmit}
            loading={importMut.isPending}
          >
            Import Holidays
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <span className={styles.titleRow}>
          <span className={styles.titleIcon} aria-hidden>
            <Flag size={16} />
          </span>
          Import UK Bank Holidays — {year}
        </span>
      }
      footer={footer}
    >
      {/* Step indicator */}
      <ol className={styles.steps} aria-label="Wizard steps">
        {[1, 2, 3].map((n) => {
          const labels = ['Select Holidays', 'Choose Employees', 'Confirm & Import'];
          const isActive = step === n;
          const isDone = step > n;
          return (
            <li
              key={n}
              className={[
                styles.stepTab,
                isActive ? styles.stepActive : '',
                isDone ? styles.stepDone : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className={styles.stepNum}>{n}</span>
              <span>{labels[n - 1]}</span>
            </li>
          );
        })}
      </ol>

      {holidaysQuery.isLoading ? (
        <div className={styles.center}>
          <Spinner />
          <p>Fetching UK bank holidays…</p>
        </div>
      ) : holidaysQuery.isError ? (
        <div className={styles.center}>
          <p className={styles.errorText}>
            Could not load bank holidays:{' '}
            {(holidaysQuery.error as Error)?.message ?? 'Unknown error'}
          </p>
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => void holidaysQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          {/* Step 1 */}
          {step === 1 ? (
            <div>
              <div className={styles.toolbar}>
                <p className={styles.helper}>
                  Select which bank holidays to assign to your team.
                </p>
                <div className={styles.toolbarActions}>
                  <Button size="sm" variant="secondary" onClick={selectAllHolidays}>
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" icon={X} onClick={deselectAllHolidays}>
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className={styles.cardGrid}>
                {(holidaysQuery.data ?? []).map((h) => {
                  const checked = selectedHolidays.has(h.date);
                  return (
                    <label
                      key={h.date}
                      className={[styles.holidayCard, checked ? styles.holidayCardSelected : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleHoliday(h.date)}
                        aria-label={`${h.title} — ${formatDate(h.date)}`}
                      />
                      <span>
                        <span className={styles.holidayName}>{h.title}</span>
                        <span className={styles.holidayMeta}>
                          {formatDate(h.date)} · {dayName(h.date)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Step 2 */}
          {step === 2 ? (
            <div>
              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Employee Type</span>
                <div className={styles.pillRow}>
                  <button
                    type="button"
                    className={[
                      styles.pill,
                      entityType === 'staff' ? styles.pillActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setEntityType('staff');
                      setSelectedEntities(new Set());
                      setSelectAllEntities(false);
                    }}
                  >
                    Staff
                  </button>
                  <button
                    type="button"
                    className={[
                      styles.pill,
                      entityType === 'driver' ? styles.pillActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setEntityType('driver');
                      setSelectedEntities(new Set());
                      setSelectAllEntities(false);
                    }}
                  >
                    Driver
                  </button>
                </div>
              </div>

              {entityType !== null ? (
                <div className={styles.fieldBlock}>
                  <div className={styles.toolbar}>
                    <span className={styles.fieldLabel}>Select Employees</span>
                    <Checkbox
                      label="Select All"
                      checked={selectAllEntities}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </div>
                  <div className={styles.cardGridSmall}>
                    {peopleList.map((p) => {
                      const checked = selectedEntities.has(p.uuid);
                      return (
                        <label
                          key={p.uuid}
                          className={[
                            styles.empCard,
                            checked ? styles.empCardSelected : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEntity(p.uuid)}
                            aria-label={p.name}
                          />
                          <span>
                            <span className={styles.empName}>{p.name}</span>
                            {p.email ? <span className={styles.empMeta}>{p.email}</span> : null}
                          </span>
                        </label>
                      );
                    })}
                    {peopleList.length === 0 ? (
                      <p className={styles.helper}>No active employees found.</p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Step 3 */}
          {step === 3 ? (
            <div>
              <div className={styles.summaryBox}>
                <h4 className={styles.summaryTitle}>Import Summary</h4>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryNum}>{selectedHolidayList.length}</span>
                    <span className={styles.summaryLabel}>Bank Holidays</span>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryNum}>{selectedEntityList.length}</span>
                    <span className={styles.summaryLabel}>Employees</span>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryNum}>
                      {selectedHolidayList.length * selectedEntityList.length}
                    </span>
                    <span className={styles.summaryLabel}>Max Records</span>
                  </div>
                </div>
                <p className={styles.infoNote}>
                  <Info size={14} aria-hidden /> Records that already exist for an employee
                  will be automatically skipped — no duplicates will be created.
                </p>
              </div>

              <div className={styles.fieldBlock}>
                <Select
                  label="Period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as HolidayPeriod)}
                  options={[
                    { value: 'FULL', label: 'Full Day' },
                    { value: 'AM', label: 'AM Only' },
                    { value: 'PM', label: 'PM Only' },
                  ]}
                />
              </div>

              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Selected Bank Holidays</span>
                <div className={styles.pillsTray}>
                  {selectedHolidayList.map((h) => (
                    <span key={h.date} className={styles.tagPill}>
                      {h.title} ({formatDate(h.date)})
                    </span>
                  ))}
                </div>
              </div>

              <div className={styles.fieldBlock}>
                <span className={styles.fieldLabel}>Selected Employees</span>
                <div className={styles.pillsTray}>
                  {selectedEntityList.map((p) => (
                    <span
                      key={p.uuid}
                      className={[styles.tagPill, styles.tagPillGreen].join(' ')}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}
